import React from 'react';

/** Map of Lucide PascalCase names to Material Icons font names */
const lucideToMui: Record<string, string> = {
  'X': 'close',
  'Pencil': 'edit',
  'Trash2': 'delete',
  'MoreVertical': 'more_vert',
  'MoreHorizontal': 'more_horiz',
  'RefreshCw': 'refresh',
  'RefreshCcw': 'sync',
  'ChevronDown': 'expand_more',
  'ChevronUp': 'expand_less',
  'AlertTriangle': 'warning',
  'AlertCircle': 'error',
  'HelpCircle': 'help',
  'Clock': 'schedule',
  'Ban': 'block',
  'User': 'person',
  'UserPlus': 'person_add',
  'UserMinus': 'person_remove',
  'Users': 'group',
  'CircleUser': 'account_circle',
  'Smile': 'face',
  'MessageCircle': 'chat',
  'MessageSquare': 'message',
  'Bell': 'bell',
  'BellRing': 'notifications_active',
  'FolderOpen': 'folder_open',
  'File': 'insert_drive_file',
  'FileText': 'description',
  'StickyNote': 'note',
  'FilePlus': 'note_add',
  'Paperclip': 'attach_file',
  'Scissors': 'content_cut',
  'Clipboard': 'content_paste',
  'Copy': 'content_copy',
  'Camera': 'photo_camera',
  'Film': 'movie',
  'Volume2': 'volume_up',
  'VolumeX': 'volume_mute',
  'Play': 'play_arrow',
  'Square': 'stop',
  'SkipBack': 'skip_previous',
  'SkipForward': 'skip_next',
  'Eye': 'visibility',
  'EyeOff': 'visibility_off',
  'List': 'view_list',
  'LayoutGrid': 'view_module',
  'Grid3X3': 'grid_view',
  'Table': 'table_chart',
  'LayoutDashboard': 'dashboard',
  'Filter': 'filter_list',
  'ArrowUpDown': 'sort',
  'Maximize': 'fullscreen',
  'Minimize': 'fullscreen_exit',
  'ZoomIn': 'zoom_in',
  'ZoomOut': 'zoom_out',
  'BarChart3': 'bar_chart',
  'PieChart': 'pie_chart',
  'TrendingUp': 'trending_up',
  'TrendingDown': 'trending_down',
  'Calendar': 'calendar_today',
  'CalendarDays': 'event',
  'Timer': 'timer',
  'AlarmClock': 'alarm',
  'History': 'history',
  'MapPin': 'location_on',
  'Map': 'map',
  'Compass': 'explore',
  'Navigation': 'navigation',
  'Globe': 'public',
  'Languages': 'language',
  'ShoppingCart': 'shopping_cart',
  'Store': 'store',
  'Tag': 'local_offer',
  'CreditCard': 'credit_card',
  'Wallet': 'payment',
  'Shield': 'security',
  'ShieldCheck': 'verified_user',
  'Lock': 'lock',
  'Unlock': 'lock_open',
  'Key': 'key',
  'Code': 'code',
  'Terminal': 'terminal',
  'Braces': 'data_object',
  'Database': 'storage',
  'Bug': 'bug_report',
  'Cloud': 'cloud',
  'Building2': 'business',
  'Briefcase': 'work',
  'ClipboardList': 'assignment',
  'ListChecks': 'checklist',
  'Layers': 'category',
  'Bot': 'smart_toy',
  'Sparkles': 'auto_awesome',
  'Lightbulb': 'lightbulb',
  'Hub': 'hub',
  'QrCode': 'qr_code',
  'Printer': 'print',
  'Share': 'share',
  'GitBranch': 'account_tree',
  'Rocket': 'rocket_launch',
  'PanelLeft': 'menu_open',
  'Package': 'inventory',
  'Shirt': 'checkroom',
};

/** Convert PascalCase to snake_case for Material Icons font, with Lucide name translation */
function toFontName(name: string): string {
  // Check Lucide-to-MUI mapping first
  if (lucideToMui[name]) {
    return lucideToMui[name];
  }
  // Already snake_case or lowercase
  if (name.includes('_') || name === name.toLowerCase()) {
    return name;
  }
  // Fall back to PascalCase → snake_case conversion
  return name
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

export interface FontIconProps {
  /** Icon name - accepts PascalCase or snake_case (e.g., "ShoppingCart" or "shopping_cart") */
  name: string;
  /** Icon size */
  size?: 'small' | 'medium' | 'large';
  /** Custom color */
  color?: string;
  /** Additional CSS class */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
}

/**
 * Material Icon font component.
 * Renders an icon using the Material Icons Outlined font.
 * Accepts both PascalCase and snake_case names.
 */
export const FontIcon: React.FC<FontIconProps> = ({
  name,
  size = 'medium',
  color,
  className = '',
  style,
}) => {
  const fontSize = size === 'small' ? 20 : size === 'large' ? 36 : 24;

  return (
    <span
      className={`material-icons-outlined ${className}`}
      style={{
        fontSize,
        color: color || 'inherit',
        verticalAlign: 'middle',
        ...style,
      }}
    >
      {toFontName(name)}
    </span>
  );
};
